import { memo, useCallback, useId, useMemo, useState } from 'react';

import { Button } from '@air/primitive-button';
import { Chip } from '@air/primitive-chip';
import { useToasts } from '@air/provider-toast';

import { useAddMembersToLibraries } from '~/components/LibraryBeta/hooks/mutations/useAddMembersToLibraries';
import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import { InviteToLibrariesStepLibraryRow } from '~/components/Modals/InviteMembers/InviteMembersModal/InviteToLibrariesStepLibraryRow';
import { useWorkspaceRolesContext } from '~/providers/WorkspaceRolesProvider';

export type InviteToLibrariesStepProps = {
  emails: string[];
  onComplete: () => void;
};

export const InviteToLibrariesStep = memo(({ emails, onComplete }: InviteToLibrariesStepProps) => {
  // @TODO(JAN-976): Add load-more support for workspaces with >100 libraries
  const { data: librariesData } = useLibraries();
  const {
    addMembersToLibraries: { mutate: sendLibraryInvites, isPending: isSendingInvites },
  } = useAddMembersToLibraries();
  const { showToast } = useToasts();
  const { generalLibraryTitle, generalLibraryIcon, generalLibraryColor } = useGeneralLibraryMetadata();
  const { data: roles } = useWorkspaceRolesContext();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const libraryListId = useId();

  const libraries = useMemo(() => librariesData?.pages.flatMap((page) => page.data) ?? [], [librariesData]);
  const selectableLibraryIds = useMemo(() => libraries.map((library) => library.id), [libraries]);
  const hasSelectableLibraries = selectableLibraryIds.length > 0;
  const areAllSelected =
    hasSelectableLibraries && selectableLibraryIds.every((libraryId) => selectedIds.has(libraryId));

  const isSinglePerson = emails.length === 1;
  const description = isSinglePerson
    ? `This user (${emails[0]}) has been invited to your workspace and the global library. You can also add them to additional libraries.`
    : `These ${emails.length} users have been invited to your workspace and the global library. You can also add them to additional libraries.`;

  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(areAllSelected ? new Set() : new Set(selectableLibraryIds));
  }, [areAllSelected, selectableLibraryIds]);

  const handleSendInvites = useCallback(() => {
    const selected = libraries
      .filter((lib) => selectedIds.has(lib.id))
      .map((lib) => ({ libraryId: lib.id, roleId: lib.defaultRoleId }));

    sendLibraryInvites(
      { emails, libraries: selected },
      {
        onSuccess: ({ succeededAttempts, failedAttempts, totalEmails, totalLibraries }) => {
          if (failedAttempts.length === 0) {
            const peopleCopy = `${totalEmails} ${totalEmails === 1 ? 'person' : 'people'}`;
            const librariesCopy = `${totalLibraries} ${totalLibraries === 1 ? 'library' : 'libraries'}`;
            showToast(`Invited ${peopleCopy} to ${librariesCopy}`);
          } else {
            const totalAttempts = totalEmails * totalLibraries;
            showToast(
              `Invited ${succeededAttempts.length} of ${totalAttempts} library memberships. ${failedAttempts.length} could not be added — try again from Library member settings.`,
            );
          }
          onComplete();
        },
        onError: () => {
          showToast('Failed to add to libraries. Please try again.');
        },
      },
    );
  }, [sendLibraryInvites, emails, libraries, onComplete, selectedIds, showToast]);

  return (
    <div className="mt-6">
      <p className="mb-4 ads:text-metadata text-14 text-gray-9">{description}</p>
      {!isSinglePerson && (
        <ul className="mb-4 flex flex-wrap gap-1.5">
          {emails.map((email) => (
            <li key={email}>
              <Chip color="light-gray" shape="rounded" size="medium">
                {email}
              </Chip>
            </li>
          ))}
        </ul>
      )}
      {hasSelectableLibraries && (
        <div className="mb-2 flex justify-end">
          <Button
            appearance="ghost"
            aria-controls={libraryListId}
            aria-label={areAllSelected ? 'Deselect all additional libraries' : 'Select all additional libraries'}
            data-testid="INVITE_TO_LIBRARIES_SELECT_ALL"
            disabled={isSendingInvites}
            onClick={handleSelectAll}
            size="medium"
          >
            {areAllSelected ? 'Deselect all' : 'Select all'}
          </Button>
        </div>
      )}
      <ul
        aria-label="Libraries"
        className="mb-6 flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-8 border ads:border-ads-gray-5 border-gray-5 p-3"
        id={libraryListId}
      >
        <li>
          <InviteToLibrariesStepLibraryRow
            id="general"
            title={generalLibraryTitle}
            colorHex={generalLibraryColor.hex}
            icon={generalLibraryIcon}
            isSelected
            disabled
            onToggle={handleToggle}
          />
        </li>
        {libraries.map((library) => (
          <li key={library.id}>
            <InviteToLibrariesStepLibraryRow
              id={library.id}
              title={library.title}
              colorHex={library.color.backgroundHex}
              icon={library.icon}
              isSelected={selectedIds.has(library.id)}
              roleName={roles?.find((r) => r.id === library.defaultRoleId)?.displayName}
              onToggle={handleToggle}
            />
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-2">
        <Button appearance="ghost" data-testid="INVITE_TO_LIBRARIES_SKIP" size="large" onClick={onComplete}>
          Skip
        </Button>
        <Button
          appearance="filled"
          data-testid="INVITE_TO_LIBRARIES_INVITE"
          disabled={selectedIds.size === 0 || isSendingInvites}
          isLoading={isSendingInvites}
          onClick={handleSendInvites}
          size="large"
        >
          Invite
        </Button>
      </div>
    </div>
  );
});

InviteToLibrariesStep.displayName = 'InviteToLibrariesStep';
